(() => {
  "use strict";

  const rowsHolderSelector = "[data-timeline-rows]";
  const rowSelector = "[data-timeline-row]";
  const rowEditorSelector = "[data-timeline-row-editor]";
  const buttonSelector = "[data-timeline-button]";
  const blockScrollSelector = "[data-block-scroll]";
  const selectedClass = "is-selected";

  class TimelineComponent extends HTMLElement {
    constructor() {
      super();

      this.rows = this.querySelectorAll(rowSelector);

      if (this.rows.length < 2) {
        return;
      }

      this.rowsWidth = 0;
      this.rowsHeight = [];
      this.holderTop = this.getBoundingClientRect().top + window.scrollY;
      this.holderHeight = this.offsetHeight;
      this.buttons = this.querySelectorAll(buttonSelector);
      this.rowsHolder = this.querySelector(rowsHolderSelector);
      this.requestAnimation = null;
      this.isDesktopView = !window.theme.isMobile();
      this.isScrollEnabled = false;

      this.scrollEvent = (event) => this.scrollEvents(event);
      this.resizeEvent = () => this.resizeEvents();
    }

    connectedCallback() {
      if (this.rows.length < 2) {
        return;
      }

      this.calculateRowsDimensions();
      this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

      if (this.isDesktopView && !this.isScrollEnabled) {
        this.isScrollEnabled = true;
        document.addEventListener("theme:scroll", this.scrollEvent);
      }

      this.rowsHolder.addEventListener("scroll", this.scrollEvent);
      document.addEventListener("theme:resize:width", this.resizeEvent);

      if (!this.buttons.length) {
        return;
      }

      this.buttons.forEach((button, index) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();

          const row = event.currentTarget.closest(rowSelector);
          if (!row) {
            return;
          }

          if (window.theme.isMobile()) {
            const scrollOffset = 16;
            const left = row.offsetLeft - scrollOffset;
            this.rowsHolder.scrollTo({ left, behavior: "smooth" });
            return;
          }

          const scrollTarget =
            this.getBoundingClientRect().top + this.getStickyHeaderHeight() + this.getDesktopOffsetForIndex(index);

          window.theme.scrollTo(scrollTarget + 1);
        });
      });
    }

    resizeEvents() {
      this.holderTop = this.getBoundingClientRect().top + window.scrollY;
      this.holderHeight = this.offsetHeight;
      this.calculateRowsDimensions();
      this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

      const isDesktopView = !window.theme.isMobile();
      if (isDesktopView && !this.isScrollEnabled) {
        this.isScrollEnabled = true;
        document.addEventListener("theme:scroll", this.scrollEvent);
      } else if (!isDesktopView && this.isScrollEnabled) {
        this.isScrollEnabled = false;
        document.removeEventListener("theme:scroll", this.scrollEvent);
      }
    }

    scrollEvents(event) {
      if (this.requestAnimation) {
        return;
      }

      const eventTarget = event.currentTarget;
      this.requestAnimation = requestAnimationFrame(() => this.calculatePosition(eventTarget));
    }

    removeAnimationFrame() {
      if (!this.requestAnimation) {
        return;
      }

      cancelAnimationFrame(this.requestAnimation);
      this.requestAnimation = null;
    }

    getDesktopOffsetForIndex(index) {
      return this.rowsHeight.slice(0, index).reduce((sum, height) => sum + height, 0);
    }

    getDesktopScrollRange() {
      return this.rowsHeight.reduce((sum, height) => sum + height, 0);
    }

    getStickyHeaderHeight() {
      const stickyHeader = document.querySelector("[data-header-sticky]");
      const headerHeight = document.querySelector("[data-header-height]");

      return stickyHeader && headerHeight ? headerHeight.offsetHeight : 0;
    }

    calculateRowsDimensions() {
      if (!this.rows.length) {
        return;
      }

      this.rowsHeight = [];
      this.rowsWidth = 0;

      let rowTopMobile = 0;
      this.rows.forEach((row, index) => {
        this.rowsHeight.push(row.offsetHeight);
        this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight, 10);

        if (!window.Shopify.designMode) {
          return;
        }

        const editorRows = this.querySelectorAll(rowEditorSelector);
        if (!editorRows.length) {
          return;
        }

        rowTopMobile += row.offsetHeight;
        editorRows[index]?.style.setProperty("--row-height-min", `${row.offsetHeight}px`);
        editorRows[index + 1]?.style.setProperty("--row-top-mobile", `${rowTopMobile}px`);
      });

      const totalHeight = this.getDesktopScrollRange();
      if (window.theme.isMobile() || !totalHeight) {
        this.style.removeProperty("--timeline-scroll-height");
        this.style.removeProperty("--timeline-body-height");
      } else {
        this.style.setProperty("--timeline-scroll-height", `${totalHeight * 2}px`);
        this.style.setProperty("--timeline-body-height", `${totalHeight}px`);
      }
    }

    calculatePosition(scrollTarget = null) {
      this.removeAnimationFrame();

      this.holderTop = this.getBoundingClientRect().top + window.scrollY;

      const isDesktopView = !window.theme.isMobile();
      const scrollProgress = Math.max(0, Math.min(this.getDesktopScrollRange(), window.scrollY - this.holderTop));

      let mobileProgress = 0;

      if (!isDesktopView && scrollTarget) {
        const left = scrollTarget.scrollLeft - 16;
        const width = scrollTarget.offsetWidth;
        mobileProgress = (left / (this.rowsWidth - width)) * 100;
        this.style.setProperty("--percent-mobile", `${mobileProgress}%`);
      }

      this.rows.forEach((row, index) => {
        if (index > 0) {
          const rowOffsetPercent = (row.offsetLeft / this.rowsWidth) * 100;
          let isSelected = mobileProgress > rowOffsetPercent;

          if (isDesktopView) {
            const rowStart = this.getDesktopOffsetForIndex(index);
            isSelected = scrollProgress >= rowStart;

            const previousRow = row.previousElementSibling;
            const previousRowHeight = this.rowsHeight[index - 1] || 0;
            const previousRowStart = rowStart - previousRowHeight;

            if (previousRow && previousRowHeight > 0) {
              const percentDesktop = Math.max(
                0,
                Math.min(100, ((scrollProgress - previousRowStart) / previousRowHeight) * 100)
              );
              previousRow.style.setProperty("--percent-desktop", `${percentDesktop}%`);
            }
          }

          row.classList.toggle(selectedClass, isSelected);
        }
      });
    }

    disconnectedCallback() {
      if (this.isScrollEnabled) {
        this.isScrollEnabled = false;
        document.removeEventListener("theme:scroll", this.scrollEvent);
      }

      this.rowsHolder.removeEventListener("scroll", this.scrollEvent);
      document.removeEventListener("theme:resize:width", this.resizeEvent);
    }

    onBlockSelect(event) {
      const blockScroller = this.querySelector(blockScrollSelector);
      if (!blockScroller) {
        return;
      }

      const block = event.srcElement;
      const index = [...block.parentElement.children].indexOf(block);
      const row = this.rows[index];

      if (row) {
        blockScroller.scrollTo({ top: 0, left: row.offsetLeft, behavior: "smooth" });
      }
    }
  }

  if (!customElements.get("timeline-component")) {
    customElements.define("timeline-component", TimelineComponent);
  }
})();
